/**
 * Les CHAMPS D'UN FORMULAIRE, tels qu'un éditeur les nomme.
 *
 * Un bloc formulaire peut envoyer des e-mails de notification dont l'objet et
 * le corps reprennent la saisie du visiteur. Le serveur y remplace des
 * marqueurs `{{ clé }}` — le NOM INTERNE du champ (`firstname`, `pageUrl`),
 * que personne n'a à connaître pour écrire un message. Ce module traduit la
 * définition du bloc en liste de champs INTITULÉS, celle que la saisie assistée
 * (`@`) déroule dans la pop-in des notifications.
 *
 * Rien n'est déclaré pour ça : tout se lit dans le rjBuild du bloc, aux deux
 * seuls endroits qui font autorité —
 *
 *  - le `submitData` dit ce qui est RÉELLEMENT posté (`data:`) : c'est la liste
 *    des clés que le serveur verra, ni plus (un champ non posté ne remplirait
 *    rien) ni moins (`pageUrl` n'a pas de champ à l'écran) ;
 *  - le champ de saisie lié au même chemin (`dataLocation`) porte l'intitulé —
 *    celui qui est AFFICHÉ, donc celui que l'éditeur a lui-même modifié en
 *    place, et non le mot figé dans la définition d'origine.
 *
 * Un nouveau bloc formulaire hérite donc de la saisie assistée sans une ligne
 * de déclaration ; c'est ce qui évite d'entretenir une liste de champs par
 * bloc, à côté du formulaire, qui divergerait au premier champ ajouté.
 */

/**
 * Clés postées qui ne sont pas de la saisie.
 *
 * Techniques (identifiant d'unité, preuve anti-robot) ou volumineuses (une
 * pièce jointe voyage en base64) : le serveur les écarte déjà de ses e-mails
 * (`FormNotifier::SKIPPED_KEYS`), les proposer serait proposer un marqueur qui
 * rend toujours vide.
 */
const TECHNICAL_KEYS = new Set(['unitid', 'captcha', 'altcha', 'attachment'])

/**
 * Intitulé des clés que le SHELL du builder ajoute à l'envoi : elles n'ont
 * aucun champ à l'écran d'où lire un libellé.
 */
const IMPLICIT_LABELS = {
  pageUrl: 'Adresse de la page',
}

/**
 * Le récapitulatif : toute la saisie, une ligne par champ.
 *
 * Ce n'est pas un champ mais c'est le marqueur le plus utile — le corps par
 * défaut d'une notification —, et il reste juste quand le formulaire gagne un
 * champ. Il se déroule donc avec les autres.
 */
export const RECAP_TOKEN = { key: 'recapitulatif', label: 'Récapitulatif de la saisie' }

/**
 * Les champs qui ont un sens comme DESTINATAIRE : ceux qui portent une adresse.
 *
 * Une colonne « destinataires » déroule la même liste que l'objet et le corps,
 * mais restreinte : insérer « Mon message » dans un `To:` ne produirait aucune
 * adresse valable, et le serveur écarte les adresses invalides EN SILENCE —
 * l'éditeur croirait avoir réglé un envoi qui ne part jamais. Le récapitulatif
 * s'écarte de lui-même : ce n'est pas une adresse.
 *
 * Le tri se fait sur le nom interne ET sur l'intitulé : un formulaire nomme son
 * champ `email` mais l'intitule « Votre courriel », ou l'inverse.
 */
export const emailTokens = (tokens) =>
  (Array.isArray(tokens) ? tokens : []).filter(
    (t) => /mail|courriel/i.test(`${t?.key ?? ''} ${t?.label ?? ''}`),
  )

/** Un chemin de données reactive-json (`~~.a.b`, `~.col`) plutôt qu'un texte. */
const isPath = (value) => typeof value === 'string' && value.startsWith('~')

/** `~~.apiData.data.objectData.data.labelLastname` → `labelLastname`. */
const lastSegment = (path) => path.split('.').filter(Boolean).pop() ?? ''

/** `firstName` → « First name » : le repli quand aucun intitulé n'est lisible. */
export const humanizeKey = (key) => {
  const spaced = String(key)
    .replace(/(?<!^)([A-Z])/g, ' $1')
    .replace(/[_\-.]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (spaced === '') return String(key)
  return spaced.charAt(0).toUpperCase() + spaced.slice(1).toLowerCase()
}

/**
 * Le texte d'une valeur d'auteur : littérale, ou lue dans les données du bloc.
 *
 * Un intitulé de champ est presque toujours une DONNÉE (`~~.….labelLastname`),
 * puisqu'il est éditable en place : le résoudre est la seule façon de dérouler
 * « Nom de famille » quand l'éditeur a renommé « Nom ».
 */
const resolveText = (value, readValue) => {
  if (typeof value !== 'string' || value.trim() === '') return ''
  if (!isPath(value)) return value.trim()
  const read = readValue?.(lastSegment(value))
  return typeof read === 'string' ? read.trim() : ''
}

/** Le premier texte d'un `content:` — chaîne, nœud, ou fragment. */
const contentText = (content, readValue) => {
  if (Array.isArray(content)) {
    for (const item of content) {
      const text = contentText(item, readValue)
      if (text) return text
    }
    return ''
  }
  if (content && typeof content === 'object') return contentText(content.content, readValue)
  return resolveText(content, readValue)
}

/**
 * L'intitulé d'un champ de formulaire reactive-json.
 *
 * `label:` est un FRAGMENT (cf. skill builder) : le premier nœud porte le
 * texte, le « * » des champs obligatoires suit — on ne garde donc que le
 * premier. Sans intitulé exploitable, le texte indicatif fait l'affaire : il
 * est lui aussi écrit pour le visiteur.
 */
const labelOfField = (node, readValue) => {
  const label = Array.isArray(node.label) ? node.label[0] : node.label
  const fromLabel = contentText(label, readValue)
  return cleanLabel(fromLabel || resolveText(node.inputAttributes?.placeholder, readValue))
}

/**
 * L'intitulé sans sa ponctuation de formulaire.
 *
 * « Prénom* », « Prénom : » sont des intitulés de CHAMP À REMPLIR ; dans une
 * liste de champs à insérer, l'astérisque d'obligation et le deux-points ne
 * veulent plus rien dire.
 */
const cleanLabel = (text) => text.replace(/[\s*:]+$/, '').trim()

/** Parcourt un rjBuild (tableaux et objets imbriqués), nœud par nœud. */
const walk = (node, visit) => {
  if (Array.isArray(node)) {
    for (const item of node) walk(item, visit)
    return
  }
  if (!node || typeof node !== 'object') return
  visit(node)
  for (const value of Object.values(node)) {
    if (value && typeof value === 'object') walk(value, visit)
  }
}

/**
 * Les champs d'un formulaire, prêts à dérouler.
 *
 * @param {object} rjBuild
 *   Le rjBuild de la DÉFINITION du bloc (`blockDef.rjBuild`).
 * @param {(fieldKey: string) => any} readValue
 *   Lecture d'un champ du bloc tel qu'il est actuellement rendu (magasin de
 *   l'unité, sinon données de la place, sinon amorce de la déf).
 *
 * @return {{key: string, label: string}[]}
 *   Dans l'ordre du `submitData`, le récapitulatif en dernier. Vide si le bloc
 *   ne poste rien : la saisie assistée s'efface alors d'elle-même.
 */
export const collectFormTokens = (rjBuild, readValue) => {
  const posted = []
  const seen = new Set()
  const labelByPath = new Map()

  walk(rjBuild?.renderView, (node) => {
    if (typeof node.dataLocation === 'string' && !labelByPath.has(node.dataLocation)) {
      const label = labelOfField(node, readValue)
      if (label) labelByPath.set(node.dataLocation, label)
    }
    if (node.what === 'submitData' && node.data && typeof node.data === 'object' && !Array.isArray(node.data)) {
      for (const [key, value] of Object.entries(node.data)) {
        if (seen.has(key) || TECHNICAL_KEYS.has(key.toLowerCase())) continue
        seen.add(key)
        posted.push({ key, path: typeof value === 'string' ? value : null })
      }
    }
  })

  if (posted.length === 0) return []

  const tokens = posted.map(({ key, path }) => ({
    key,
    label: (path && labelByPath.get(path)) || IMPLICIT_LABELS[key] || humanizeKey(key),
  }))
  tokens.push({ ...RECAP_TOKEN })
  return tokens
}
